import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  type InfiniteData,
  type QueryClient,
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";

import { api } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";
import { getNextPaginatePage, insertDataOnInfiniteQuery } from "@/utils";

import type { Conversation, UnreadCountResponse } from "../types";

export type ConversationList = Paginate<Conversation<true, true, true>>;

export const conversations = createQueryKeys("messenger-conversations", {
  allInfinite: (filter?: SearchRequest) => [{ filter }],
  user: (id?: number) => [id],
  one: (id?: number) => [id],
  unreadCount: null,
});

const include = "group,last_messages,user.position";

export const fetchConversationsQueryOptions = (search?: SearchRequest) =>
  infiniteQueryOptions({
    ...conversations.allInfinite(search),
    queryFn: ({ pageParam }) =>
      api
        .get<ConversationList>(`/messenger/conversations`, {
          params: { page: pageParam, include, ...search },
        })
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: getNextPaginatePage,
  });

export const useFetchConversations = (search?: SearchRequest) =>
  useInfiniteQuery({ ...fetchConversationsQueryOptions(search) });

export const fetchUnreadConversationsCountQueryOptions = () =>
  queryOptions({
    ...conversations.unreadCount,
    queryFn: () =>
      api
        .get<UnreadCountResponse>("/messenger/conversations/unread-count")
        .then((response) => response.data),
  });

export const useFetchUnreadConversationsCount = () =>
  useQuery(fetchUnreadConversationsCountQueryOptions());

export function isPrivateConversationWithUser(
  conversation: Conversation<true, true, true> | null | undefined,
  userId: number | undefined,
): conversation is Extract<
  Conversation<true, true, true>,
  { type: "private" }
> {
  return (
    userId !== undefined &&
    conversation?.type === "private" &&
    conversation.user.id === userId
  );
}

export const fetchConversationOfUser = (userId: number | undefined) =>
  queryOptions({
    ...conversations.user(userId),
    queryFn: (): Promise<Conversation<true, true, true> | null> =>
      api
        .get<Conversation<true, true, true>>(
          `/messenger/conversations/users/${userId}`,
          {
            params: { include },
          },
        )
        .then((response) => {
          const conversation = response.data;
          if (isPrivateConversationWithUser(conversation, userId)) {
            return conversation;
          }
          return null;
        })
        .catch((error) => {
          // No private conversation yet. Null (not undefined) — Query v5 rejects undefined.
          if (error.response?.status === 404) {
            return null;
          }
          throw error;
        }),
  });

export const useFetchConversationOfUser = (userId: number | undefined) =>
  useQuery({
    ...fetchConversationOfUser(userId),
    enabled: userId !== undefined,
  });

export const fetchConversationByIdQueryOptions = (id: number | undefined) =>
  queryOptions({
    ...conversations.one(id),
    queryFn: () =>
      api
        .get<Conversation<true, true, true>>(`/messenger/conversations/${id}`, {
          params: { include },
        })
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchConversationById = (id: number | undefined) =>
  useQuery({
    ...fetchConversationByIdQueryOptions(id),
    enabled: id !== undefined,
  });

export const upsertConversationInLists = (
  queryClient: QueryClient,
  conversation: Conversation<true, true, true>,
) => {
  const upsert = (filter?: SearchRequest) => {
    queryClient.setQueryData<InfiniteData<ConversationList>>(
      conversations.allInfinite(filter).queryKey,
      (old) => {
        if (!old?.pages?.[0]) return old;

        const withoutCurrent = {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            data: page.data.filter((item) => item.id !== conversation.id),
          })),
        };

        return insertDataOnInfiniteQuery(withoutCurrent, conversation, "start");
      },
    );
  };

  upsert();
  if (conversation.type === "group") {
    upsert({ filters: { type: "group" } });
  }
};
